import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  LEGACY_CAPABILITY_ID,
  type FactoryEdit,
  type LegacyFactory,
} from '@indutrust/factory-model';
import { legacyToFactoryEdit } from '@indutrust/factory-util';
import { EnvironmentService } from '@indutrust/shared-env';
import { map, type Observable } from 'rxjs';

/**
 * Payload accepted by `POST /factories/create_from_api`.
 *
 * The backend accepts a free-form payload describing an external factory
 * reference to import. We keep the shape open (`Record<string, unknown>`)
 * because the fields are still evolving on the backend side — this service
 * is a thin wrapper, the contract is verified by the caller (typically a
 * form).
 */
export type FactoryImportPayload = Record<string, unknown>;

/**
 * API client for factory action/workflow endpoints that do not fit the
 * `BaseApiClient<T>` REST shape:
 *
 * - `POST /factories/create_from_api`
 * - `PATCH /factories/{id}/submit_for_approval`
 * - `PATCH /factories/{id}/publish`
 * - `PATCH /factories/{id}/update_logo`
 * - `PATCH /factories/{id}/update_capabilities`
 * - `POST /settings/technologies` / `DELETE /settings/technologies/{id}`
 *   (Technology leaf writes, always with {@link LEGACY_CAPABILITY_ID})
 *
 * Soft-delete (`DELETE /factories/{id}`) is kept on the base REST client;
 * the backend handles the soft flag server-side.
 */
@Service()
export class FactoryActionsApiClient {
  private readonly envService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  /**
   * Imports a factory from an external API provider.
   * @param payload - Import {@link FactoryImportPayload}.
   * @returns An observable emitting the created {@link FactoryEdit}.
   */
  createFromApi(payload: FactoryImportPayload): Observable<FactoryEdit> {
    return this.http
      .post<LegacyFactory>(`${this.baseUrl}/factories/create_from_api`, payload)
      .pipe(map((wire) => legacyToFactoryEdit(wire)));
  }

  /**
   * Deletes a Technology leaf link created via {@link linkSubTechnologies}.
   * @param linkId - Id returned by the settings technologies create endpoint.
   */
  deleteSubTechnologyLink(linkId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/settings/technologies/${linkId}`
    );
  }

  /**
   * Links Technology leaves (legacy `sub_technology_ids`) to a factory under
   * the fixed {@link LEGACY_CAPABILITY_ID}.
   *
   * This is the only available leaf write path on the legacy backend.
   */
  linkSubTechnologies(
    factoryId: number,
    subTechnologyIds: number[]
  ): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/settings/technologies`, {
      sub_technology_ids: subTechnologyIds,
      factory_id: factoryId,
      capability_id: LEGACY_CAPABILITY_ID,
    });
  }

  /**
   * Publishes a previously approved factory. Only available for admin users.
   * @param factoryId - Id of the factory to publish.
   * @returns An observable emitting the updated {@link FactoryEdit}.
   */
  publish(factoryId: number): Observable<FactoryEdit> {
    return this.http
      .patch<LegacyFactory>(
        `${this.baseUrl}/factories/${factoryId}/publish`,
        {}
      )
      .pipe(map((wire) => legacyToFactoryEdit(wire)));
  }

  /**
   * Submits a factory for approval by an admin reviewer.
   * @param factoryId - Id of the factory being submitted.
   * @returns An observable emitting the updated {@link FactoryEdit}.
   */
  submitForApproval(factoryId: number): Observable<FactoryEdit> {
    return this.http
      .patch<LegacyFactory>(
        `${this.baseUrl}/factories/${factoryId}/submit_for_approval`,
        {}
      )
      .pipe(map((wire) => legacyToFactoryEdit(wire)));
  }

  /**
   * Updates the four top-level legacy capability flags via
   * `PATCH /factories/{id}/update_capabilities`.
   *
   * @param factoryId - Id of the factory to update.
   * @param capabilityIdsArray - Legacy capability ids (typically including
   *   {@link LEGACY_CAPABILITY_ID}).
   */
  updateCapabilities(
    factoryId: number,
    capabilityIdsArray: number[]
  ): Observable<{ current_capabilities: number[] }> {
    return this.http.patch<{ current_capabilities: number[] }>(
      `${this.baseUrl}/factories/${factoryId}/update_capabilities`,
      {
        factory: {
          capability_ids_array: capabilityIdsArray,
        },
      }
    );
  }

  /**
   * Uploads a new logo for the factory.
   * @param factoryId - Id of the factory to update.
   * @param logo - The logo file to upload.
   * @returns An observable emitting the updated {@link FactoryEdit}.
   */
  updateLogo(factoryId: number, logo: File): Observable<FactoryEdit> {
    const form = new FormData();
    form.append('logo', logo);
    return this.http
      .patch<LegacyFactory>(
        `${this.baseUrl}/factories/${factoryId}/update_logo`,
        form
      )
      .pipe(map((wire) => legacyToFactoryEdit(wire)));
  }

  private get baseUrl(): string {
    return this.envService.getApiUrl();
  }
}
